import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { trackEvent } from '../../../lib/analytics';

import { useShortKeysNavigation } from '../../../hooks/useShortKeysNavigation';

import {
    toggleDiagramCreateNew,
    toggleDiagramImportSql,
} from '../../../redux/main/main.actions';
import {
    selectDiagramsList,
    selectIsDiagramsListLoading,
} from '../../../redux/main/main.selectors';
import { selectCurrentWorkspace } from '../../../redux/workspace/workspace.selectors';

import Notify from '../../../components/Notify/Notify';
import ModalProfileMenu from '../../../components/Modals/ModalProfileMenu/ModalProfileMenu';
import ModalWorkspaceMenu from '../../../components/Modals/ModalWorkspaceMenu/ModalWorkspaceMenu';

import ModalPricingPlanLimit from '../../../components/Modals/ModalPricingPlanLimit/ModalPricingPlanLimit';
import ModalPricingPlanUpgrade from '../../../components/Modals/ModalPricingPlanUpgrade/ModalPricingPlanUpgrade';

import ModalInviteToApp from '../../../components/Modals/ModalInvitePeople/ModalInvitePeople';
import ModalFeedbackThanks from '../../../components/Modals/ModalFeedbackThanks/ModalFeedbackThanks';
import ModalSupport from '../../../components/Modals/ModalFeedback/ModalFeedback';

import ModalDiagramCreate from '../../../components/Modals/ModalDiagramCreate/ModalDiagramCreate';
import ModalDiagramImportSql from '../../../components/Modals/ModalDiagramImportSql/ModalDiagramImportSql';

import Sidebar from '../_components/Sidebar/Sidebar';
import Header from '../_components/Header/Header';

import ButtonImportSql from '../_components/ButtonImportSql/ButtonImportSql';
import DiagramGrid from '../_components/DiagramGrid/DiagramGrid';
import DiagramGridSkeleton from '../_components/DiagramGrid/DiagramGridSkeleton';
import DiagramGridEmpty from '../_components/DiagramGrid/DiagramGridEmpty';

import '../MainApp.scss';
import './Dashboard.scss';
import ModalDiagramCreateNew from '../../../components/Modals/ModalDiagramCreateNew/ModalDiagramCreateNew';
import ModalDiagramDeleteSoft from '../../../components/Modals/ModalDiagramDeleteSoft/ModalDiagramDeleteSoft';
import ModalDiagramRestore from '../../../components/Modals/ModalDiagramRestore/ModalDiagramRestore';
import ModalWorkspaceCreateNew from '../../../components/Modals/ModalWorkspaceCreateNew/ModalWorkspaceCreateNew';

import ModalKeyboardShortcuts from '../../../components/Modals/ModalKeyboardShortcuts/ModalKeyboardShortcuts';
import ModalLanguages from '../../../components/Modals/ModalLanguages/ModalLanguages';

function filterData(data = null, type = 'all') {
    if (!data || !Array.isArray(data)) {
        return null;
    }

    return data.filter((diagram) => {
        switch (type) {
            case 'all': return !diagram.archived_at;
            case 'mydiagrams': return diagram.access_role === 'owner' && !diagram.archived_at;
            case 'shared': return diagram.access_role !== 'owner' && !diagram.archived_at;
            case 'archived': return !!diagram.archived_at;
            default: return false;
        }
    });
}

function filterEmptyCopy(type, t) {
    switch (type) {
        case 'mydiagrams': return t('dashboard.empty_mydiagrams');
        case 'shared': return t('dashboard.empty_shared');
        case 'archived': return t('dashboard.empty_archived');
        case 'all':
        default: return t('dashboard.empty_all');
    }
}

const Dashboard = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        document.title = t('dashboard.doc_title');
    }, [t, i18n.language]);

    const dispatch = useDispatch();
    const history = useHistory();

    const { isMac } = useShortKeysNavigation();

    const [selectedType, setSelectedType] = useState('all');

    const currentWorkspace = useSelector(selectCurrentWorkspace);
    const diagramsRedux = useSelector(selectDiagramsList);
    const isDiagramsLoading = useSelector(selectIsDiagramsListLoading);


    const [diagrams, setDiagrams] = useState(null);

    useEffect(() => {
        if (!filterData(diagramsRedux, 'archived')?.length && selectedType === 'archived') {
            setSelectedType('all');
        }

        setDiagrams(diagramsRedux);
    }, [diagramsRedux]);

    const handleCreateDiagram = () => {
        trackEvent('app_diagrams_btn_create', {
            page: 'app',
            type: 'diagrams',
            subtype: 'buttons',
        });
        dispatch(toggleDiagramCreateNew('main'));
    };
    const handleImportClick = () => {
        trackEvent('app_diagrams_btn_import', {
            page: 'app',
            type: 'diagrams',
            subtype: 'buttons',
        });
    };

    const handleImportSql = () => {
        trackEvent('app_diagrams_btn_import_sql', {
            page: 'app',
            type: 'diagrams',
            subtype: 'buttons',
        });
        dispatch(toggleDiagramImportSql('sql'));
    };
    const handleImportDbml = () => {
        trackEvent('app_diagrams_btn_import_dbml', {
            page: 'app',
            type: 'diagrams',
            subtype: 'buttons',
        });
        dispatch(toggleDiagramImportSql('dbml'));
    };
    const handleImportPlayground = () => {
        trackEvent('app_diagrams_btn_import_playgr', {
            page: 'app',
            type: 'diagrams',
            subtype: 'buttons',
        });
        dispatch(toggleDiagramImportSql('playground'));
    };

    useEffect(() => {
        // Block browser defaults for these combos (Ctrl/Cmd-only and Ctrl/Cmd+Shift).
        // Keystrokes here are claimed by the app — they should not trigger bookmarks,
        // view-source, downloads, DevTools, etc. while the dashboard is active.
        const ctrlStopCodes = new Set([
            'KeyQ', 'KeyY', 'KeyU', 'KeyI', 'KeyJ', 'Quote', 'KeyB',
            'KeyS', 'KeyP', 'KeyL', 'KeyG', 'KeyH',
            // Reserved for future app shortcuts:
            'KeyO', 'KeyM', 'Period', 'Comma', 'KeyF', 'KeyK',
        ]);
        const ctrlShiftStopCodes = new Set(['KeyI', 'KeyC']);

        const handleKeyDown = (e) => {
            const isCtrl = isMac ? e.metaKey : e.ctrlKey;

            if (isCtrl && e.shiftKey && (e.code === 'KeyO' || e.key.toLowerCase() === 'o')) {
                e.preventDefault();
                dispatch(toggleDiagramCreateNew('main'));
                return;
            }

            if (isCtrl && e.shiftKey && ctrlShiftStopCodes.has(e.code)) {
                e.preventDefault();
                return;
            }

            if (isCtrl && !e.shiftKey && ctrlStopCodes.has(e.code)) {
                e.preventDefault();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dispatch, isMac]);

    const handleSelectType = (value) => {
        trackEvent(`app_diagrams_select_${value}`, {
            page: 'app',
            type: 'diagrams',
        });
        setSelectedType(value);
    };

    return (
        <div className="dash-container Dashboard">
            <Sidebar />
            <div className='dash-main'>

                {/* common modals */}
                <Notify viewType='main' />
                <ModalProfileMenu />
                <ModalWorkspaceMenu />

                <ModalPricingPlanLimit />
                <ModalPricingPlanUpgrade />

                <ModalInviteToApp />
                <ModalFeedbackThanks />
                <ModalSupport />

                {/* page components */}
                <ModalDiagramCreate />
                <ModalDiagramImportSql />

                {/* ModalDiagramClone/Rename/Share/Delete/Details are mounted in Sidebar */}
                <ModalDiagramDeleteSoft />
                <ModalDiagramRestore />

                <ModalWorkspaceCreateNew />

                <ModalKeyboardShortcuts />
                <ModalDiagramCreateNew /> {/* should be after <ModalKeyboardShortcuts />  */}

                <ModalLanguages />

                <Header title=''>
                    {/* <ButtonImportSql handleClick={(e) => { handleImportClick(e); }} handleSqlClick={(e) => { handleImportSql(e); }} handleDbmlClick={(e) => { handleImportDbml(e); }} handlePlaygroundClick={(e) => { handleImportPlayground(e); }} /> */}
                    <button className='dash-main-header-button prime new' onClick={(e) => { handleCreateDiagram(e); }}>
                        {/* <svg width="14px" height="15px" viewBox="0 0 24 24" fill="black" stroke="black" strokeWidth="2.4px" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '5px' }}><path d="M12 5v14M5 12h14"></path></svg> */}
                        {t('t.ctrl_top_left.new_diagram')}
                    </button>
                </Header>

                <div className='dash-main-body'>

                    {/* diagrams */}
                    {/* <div className='dash-main-body-title'>Diagrams
                        <span>({filterData(diagrams, selectedType)?.length || 0})</span>
                    </div> */}

                    <div className='selection-group'>
                        <div className={`selection-item ${selectedType === 'all' ? 'selected' : ''}`} onClick={() => handleSelectType('all')}>{t('dashboard.filter_all')}</div>
                        <div className={`selection-item ${selectedType === 'mydiagrams' ? 'selected' : ''}`} onClick={() => handleSelectType('mydiagrams')}>{t('dashboard.filter_mydiagrams')}</div>
                        <div className={`selection-item ${selectedType === 'shared' ? 'selected' : ''}`} onClick={() => handleSelectType('shared')}>{t('dashboard.filter_shared')}</div>
                        {filterData(diagrams, 'archived')?.length ?
                            <React.Fragment>
                                <div className="selection-item-slash"></div>
                                <div className={`selection-item ${selectedType === 'archived' ? 'selected' : ''}`} onClick={() => handleSelectType('archived')}>{t('dashboard.filter_archived')}</div>
                            </React.Fragment>
                            : ''}
                    </div>

                    {/* <div className='dash-main-body-title'>
                        My diagrams
                        <span>({templates?.length || 0})</span>
                        <span className='button' onClick={(e) => {
                            setTimeout(() => { history.push('/templates/'); }, 100);
                        }}>New Diagram</span>
                    </div> */}

                    <div className={`diagrams-group ${!filterData(diagramsRedux, selectedType)?.length ? 'diagrams-group-empty' : ''}`}>
                        {(() => {
                            if (isDiagramsLoading || diagramsRedux == null) {
                                return null;
                            }
                            const filteredList = (filterData(diagramsRedux, selectedType) || [])
                                .slice()
                                .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }));
                            // .sort((a, b) => {
                            //     const aTime = a.last_edited_at ? new Date(a.last_edited_at.replace(' ', 'T')).getTime() : 0;
                            //     const bTime = b.last_edited_at ? new Date(b.last_edited_at.replace(' ', 'T')).getTime() : 0;
                            //     return bTime - aTime;
                            // });

                            if (filteredList.length === 0) {
                                return <div className="diagrams-empty-filter">{filterEmptyCopy(selectedType, t)}</div>;
                            }
                            return filteredList.map((diagram) => (
                                <DiagramGrid key={diagram.id} data={diagram} selectedFilter={selectedType} />
                            ));
                        })()}
                    </div>

                    {/* shared diagrams */}
                    {/* <div className='dash-main-body-title'>Shared with me <span>({sharedDiagrams?.length || 0})</span></div>
                    {sharedDiagrams?.length ?
                        <div className="diagrams-group">
                            {sharedDiagrams.map((diagram) => <DiagramGrid data={diagram} />)}
                        </div>
                        : ''
                    } */}

                    {/* templates */}
                    {/* <div className='dash-main-body-title'>
                        Templates
                        <span>({templates?.length || 0})</span>
                        <span className='button' onClick={(e) => {
                            setTimeout(() => { history.push('/templates/'); }, 100);
                        }}>See more</span>
                    </div>
                    <div className="diagrams-group">
                        {templates.map((template) => <TemplateGrid data={template} />)}
                    </div> */}

                </div>

            </div>
        </div>
    );
};

export default Dashboard;
